import { defineStore } from 'pinia';
import resourceManuals from '@module/resourceManuals.cjs';

import { MenuInterface, BreadcrumbInterface } from '@interface/page';
import {
  RequestGetNewsList, ResponseGetNewsList, AxiosGetNewsList
} from '@server/apiNews';

export interface PageStateInterface { // 平台状态
  digitalHumanOpen: boolean; // 客服展开时，移动端暂停展示底部预约条
  menu: { // 菜单
    isOpen: boolean; // 是否展开
    openKey: string[]; // 展开的Key
    selectKey: string[]; // 选择的Key
    list: MenuInterface[]; // 列表
  };
  breadcrumb: BreadcrumbInterface[]; // 面包屑
  modal: { [key: string]: { open: Function, close: Function } }; // 对话框
}

/**
 * 页面数据
 */
export default defineStore('page', {
  state: (): PageStateInterface => {
    return {
      digitalHumanOpen: false,
      menu: {
        isOpen: false,
        openKey: [],
        selectKey: [],
        list: [
          {
            name: 'page.menu.home', // 使用国际化 key
            href: $Enum.RouterEnum.Home,
          },
          {
            name: 'page.menu.products',
            children: [
              {
                name: 'page.menu.productsOverview',
                desc: 'page.menuDesc.productsOverview',
                icon: 'AppstoreOutlined',
                href: $Enum.RouterEnum.Products
              },
              {
                name: 'page.menu.productTerminal',
                desc: 'page.menuDesc.productTerminal',
                icon: 'RobotOutlined',
                href: $Enum.RouterEnum.ProductTerminal
              },
              {
                name: 'page.menu.personal',
                desc: 'page.menuDesc.personal',
                icon: 'HomeOutlined',
                href: $Enum.RouterEnum.Personal
              },
            ]
          },
          {
            name: 'page.menu.solutions',
            children: [
              {
                name: 'page.menu.solutionsOverview',
                desc: 'page.menuDesc.solutionsOverview',
                icon: 'AppstoreOutlined',
                href: $Enum.RouterEnum.Solutions
              },
              {
                name: 'page.menu.solutionRetail',
                desc: 'page.menuDesc.solutionRetail',
                icon: 'ShopOutlined',
                href: $Enum.RouterEnum.SolutionRetail
              },
              {
                name: 'page.menu.solutionGovernment',
                desc: 'page.menuDesc.solutionGovernment',
                icon: 'BankOutlined',
                href: $Enum.RouterEnum.SolutionGovernment
              },
              {
                name: 'page.menu.solutionMuseum',
                desc: 'page.menuDesc.solutionMuseum',
                icon: 'CompassOutlined',
                href: $Enum.RouterEnum.SolutionMuseum
              },
              {
                name: 'page.menu.solutionMerchant',
                desc: 'page.menuDesc.solutionMerchant',
                icon: 'GlobalOutlined',
                href: $Enum.RouterEnum.SolutionMerchant
              },
            ]
          },
          {
            name: 'page.menu.cases',
            href: $Enum.RouterEnum.Cases,
          },
          {
            name: 'page.menu.tech',
            children: [
              {
                name: 'page.menu.techArch',
                desc: 'page.menuDesc.techArch',
                icon: 'PartitionOutlined',
                href: $Enum.RouterEnum.Tech,
              },
              {
                name: 'page.menu.aiDigitalHuman',
                desc: 'page.menuDesc.aiDigitalHuman',
                icon: 'RobotOutlined',
                href: $Config.Href.aiavatars
              },
              {
                name: 'page.menu.aiAppMarket',
                desc: 'page.menuDesc.aiAppMarket',
                icon: 'AppstoreOutlined',
                href: $Config.Href.store
              },
              {
                name: 'page.menu.aiPlatform',
                desc: 'page.menuDesc.aiPlatform',
                icon: 'RocketOutlined',
                href: $Enum.RouterEnum.ProductAIPilot
              },
              {
                name: 'page.menu.aiHostingPlatform',
                desc: 'page.menuDesc.aiHostingPlatform',
                icon: 'CloudServerOutlined',
                href: $Enum.RouterEnum.ProductInfraPilot
              },
              {
                name: 'page.menu.operationTrusteeship',
                desc: 'page.menuDesc.operationTrusteeship',
                icon: 'MonitorOutlined',
                href: $Enum.RouterEnum.PlanTrusteeship
              },
              {
                name: 'page.menu.costOptimization',
                desc: 'page.menuDesc.costOptimization',
                icon: 'PieChartOutlined',
                point: false,
                href: $Enum.RouterEnum.PlanCost
              },
            ]
          },
          {
            name: 'page.menu.resources',
            children: [
              {
                name: 'router.database',
                icon: 'ReadOutlined',
                href: '/database/index.html',
                native: true
              },
              {
                name: 'page.menu.productManual',
                group: true,
                children: resourceManuals.map(item => ({
                  name: item.name,
                  desc: item.desc,
                  icon: item.icon,
                  ...(item.requiresLogin ? {
                    click: () => $RQ.SSO.check() ? open(item.href) : $RQ.SSO.login()
                  } : { href: item.href })
                }))
              }
            ]
          },
          {
            name: 'page.menu.about',
            point: false,
            children: [
              {
                name: 'page.menu.companyProfile',
                desc: 'page.menuDesc.companyProfile',
                icon: 'ProfileOutlined',
                href: $Enum.RouterEnum.About,
                point: false
              },
              {
                name: 'page.menu.news',
                desc: 'page.menuDesc.news',
                icon: 'NotificationOutlined',
                href: '/v2/news/list.html',
                native: true,
                point: false
              }
            ]
          }
        ]
      },
      breadcrumb: [],
      modal: {}
    };
  },
  getters: { // 获取
  },
  actions: { // 方法
    /**
     * 更新菜单
     * @param {*} data 数据
     */
    updateMenu(data: any): void {
      $FN.Object.synchronize(this.menu, data);
    },

    /**
     * 更新面包屑
     * @param {BreadcrumbInterface[]} data 数据
     */
    updateBreadcrumb(data: BreadcrumbInterface[]): void {
      $FN.Object.synchronize(this.breadcrumb, data);
    },

    //----- 对话框 Start -----//
    /**
     * 更新对话框
     * @param {*} data 数据
     */
    updateModal(data: any): void {
      this.modal = data;
    },

    /**
     * 开启对话框
     * @param {string} name 名称
     * @param {any} data 数据
     */
    openModal(name: string, data: any = null): void {
      this.modal[name]?.open(data);
    },

    /**
     * 关闭对话框
     * @param {string} name 名称
     * @param {any} data 数据
     */
    closeModal(name: string, data: any = null): void {
      this.modal[name]?.close(data);
    },
    //----- 对话框 End -----//

    /**
     * 检查红点-解决方案
     * @param {string} router 路由
     */
    async checkPointPlan(router: string = ''): Promise<any> {
      const menu = this.menu.list.find((v: MenuInterface) => v.name === 'page.menu.tech'),
        point: any = {
          [$Enum.RouterEnum.PlanOperation]: false,
          [$Enum.RouterEnum.PlanCost]: false,
          [$Enum.RouterEnum.PlanStable]: false,
          [$Enum.RouterEnum.PlanMigrate]: false,
          [$Enum.RouterEnum.PlanActive]: false,
          [$Enum.RouterEnum.PlanTrusteeship]: true
        };
      let pointLocal: any = $FN.LocalStorage.get($Enum.StorageEnum.PointPlan);

      pointLocal = pointLocal
        ? JSON.parse(pointLocal)
        : {};
      Object.keys(point)
        .forEach(v => {
          if (pointLocal[v] === undefined) return;
          point[v] = pointLocal[v];
        });

      router !== '' && (point[router] = false);
      $FN.LocalStorage.set({
        [$Enum.StorageEnum.PointPlan]: JSON.stringify(point)
      });

      menu.point = Object.keys(point).some(v => point[v]);
      menu.children.forEach((v: MenuInterface) => {
        v.point = point[v.href];
      });
    },

    /**
     * 检查红点-最新动态
     * @param {boolean} isUpdate 是否更新
     */
    async checkPointNews(isUpdate: boolean = false): Promise<any> {
      await AxiosGetNewsList({
        page_number: 1,
        page_size: 1
      } as RequestGetNewsList)
        .then((response: any) => {
          const data: ResponseGetNewsList = response.data,
            about = this.menu.list.find((v: MenuInterface) => v.name === 'page.menu.about');
          let point = false;

          if (data.code !== $Enum.CodeEnum.Normal) return;

          if (isUpdate) {
            $FN.LocalStorage.set({
              [$Enum.StorageEnum.PointNews]: data.data.article_data[0].article_id
            });
          } else {
            point = data.data.article_data[0].article_id !== $FN.LocalStorage.get($Enum.StorageEnum.PointNews);
          }

          about.children.find((v: MenuInterface) => v.name === 'page.menu.news').point = point;
          about.point = about.children.some((v: MenuInterface) => v.point);
        });
    },

    /**
     * 检查红点-公司简介
     * @param {boolean} isUpdate 是否更新
     */
    async checkPointCompany(isUpdate: boolean = false): Promise<any> {
      const date = '20240110',
        about = this.menu.list.find((v: MenuInterface) => v.name === 'page.menu.about');
      let point = false;

      if (isUpdate) {
        $FN.LocalStorage.set({
          [$Enum.StorageEnum.PointCompany]: date
        });
      } else {
        point = date !== $FN.LocalStorage.get($Enum.StorageEnum.PointCompany);
      }

      about.children.find((v: MenuInterface) => v.name === 'page.menu.companyProfile').point = point;
      about.point = about.children.some((v: MenuInterface) => v.point);
    }
  }
});
